import {
  ButtonGroup,
  DataStatus,
  Form,
  FormButton,
  FormSubmitHandler,
  isCurrency,
  openDialog,
  Tooltip,
} from 'platform/components';
import {Heading, Hide, Space, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useSelector} from 'react-redux';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {selectTenant} from '@dms/api/core';
import {
  GetServiceOrderItemsDiscountApiResponse,
  GetSmallUnitMaterialCustomRecalculationApiResponse,
  useGetServiceOrderCustomerContractDetailsQuery,
  useGetServiceOrderItemsDiscountQuery,
  useGetServiceOrderQuery,
  useGetSmallUnitMaterialCustomRecalculationQuery,
  usePatchSmallUnitMaterialCustomRecalculationMutation,
  usePutServiceOrderItemsDiscountMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {
  DEFAULT_CURRENCY,
  getDecimalFromPercentage,
  getPercentageFromDecimal,
  handleApiError,
} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {discountSectionSchema, DiscountSectionFormType} from '../types/discountSectionSchema';
import {MaterialDiscountOption} from '../types/materialDiscountOption';
import {WorkDiscountOption} from '../types/workDiscountOption';
import {DiscountConfirmationDialogContent} from './DiscountConfirmationDialogContent';
import {DiscountOnMaterialCard} from './DiscountOnMaterialCard';
import {DiscountOnWorkCard} from './DiscountOnWorkCard';
import {SmallUnitMaterialCard} from './SmallUnitMaterialCard';

interface DiscountSectionProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  isReadOnly?: boolean;
  canEditDiscount: boolean;
}

export function DiscountSection(props: DiscountSectionProps) {
  const {data, isLoading, isError} = useGetServiceOrderItemsDiscountQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });
  const {
    data: contract,
    isFetching: isContractFetching,
    isError: isContractError,
  } = useGetServiceOrderCustomerContractDetailsQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });
  const {
    data: smallUnitMaterialData,
    isLoading: isSmallUnitMaterialLoading,
    isError: isSmallUnitMaterialError,
  } = useGetSmallUnitMaterialCustomRecalculationQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });

  return (
    <DataStatus
      isLoading={isLoading || isContractFetching || isSmallUnitMaterialLoading}
      isError={isError || isContractError || isSmallUnitMaterialError}
      minHeight={45}
    >
      <Heading size={4}>{i18n.t('entity.order.labels.customDiscounts')}</Heading>
      <Space vertical={1} />
      <Text size="small" color="secondary">
        {i18n.t('entity.order.labels.discountInfo')}
      </Text>
      <Space vertical={4} />
      <InnerForm
        data={data}
        smallUnitMaterialData={smallUnitMaterialData}
        isDisabled={isNotNil(contract)}
        {...props}
      />
    </DataStatus>
  );
}

interface InnerFormProps extends DiscountSectionProps {
  data: GetServiceOrderItemsDiscountApiResponse | Nullish;
  smallUnitMaterialData: GetSmallUnitMaterialCustomRecalculationApiResponse | Nullish;
  isDisabled: boolean;
}

function InnerForm(props: InnerFormProps) {
  const {data: selectedTenant} = useSelector(selectTenant);

  const {data: serviceOrder} = useGetServiceOrderQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });

  const currency = isCurrency(selectedTenant?.currency)
    ? selectedTenant?.currency
    : DEFAULT_CURRENCY;

  const [putServiceOrderItemsDiscount] = usePutServiceOrderItemsDiscountMutation();
  const [patchSmallUnitMaterialCustomRecalculation] =
    usePatchSmallUnitMaterialCustomRecalculationMutation();

  const buildServiceOrderDiscount = (data: DiscountSectionFormType) => ({
    workDiscount: match([data.selectedWorkOption, data.isWorkExpanded])
      .with([WorkDiscountOption.PercentageDiscount, true], () => ({
        percentageDiscount: getDecimalFromPercentage(data.work?.percentDiscount?.discountRate),
      }))
      .with([WorkDiscountOption.DiscountedWorkRate, true], () => ({
        discountedWorkRate:
          data.work?.discountedWorkRates?.discountedWorkRate?.map((rate) => ({
            workType: rate.workRateType ?? '',
            workRateWithoutVat: isNil(rate?.workRatePriceWithoutVat)
              ? null
              : {
                  amount: rate.workRatePriceWithoutVat,
                  currency: String(currency),
                },
          })) ?? undefined,
      }))
      .otherwise(() => undefined),
    materialDiscount: match([data.selectedMaterialOption, data.isMaterialExpanded])
      .with([MaterialDiscountOption.DiscountOnSalePrice, true], () => ({
        discountToSalePrice: getDecimalFromPercentage(
          data.material?.percentDiscount?.discountOnSalePriceRate
        ),
      }))
      .with([MaterialDiscountOption.MarginToPurchasePrice, true], () => ({
        markupToPurchasePrice: getDecimalFromPercentage(
          data.material?.percentDiscount?.discountMarginToPurchasePriceRate
        ),
      }))
      .otherwise(() => undefined),
  });

  const submitDiscountDirectly = async (data: DiscountSectionFormType) => {
    const serviceOrderDiscount = buildServiceOrderDiscount(data);

    await putServiceOrderItemsDiscount({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
      body: {
        ...serviceOrderDiscount,
        isDiscountApply: true,
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleSubmit: FormSubmitHandler<DiscountSectionFormType> = async (data) => {
    if (props.isDisabled) {
      return;
    }

    const fixedValueAmount = data.isSmallUnitMaterialExpanded
      ? (data.smallUnitMaterial?.fixedValueAmountWithVat ??
        data.smallUnitMaterial?.fixedValueAmountWithoutVat)
      : undefined;

    await patchSmallUnitMaterialCustomRecalculation({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
      body: {
        isFixedValueEnabled: data.isSmallUnitMaterialExpanded,
        isFixedValueWithVat: data.isSmallUnitMaterialExpanded ? data.isFixedValueWithVat : false,
        fixedValueAmount,
      },
    })
      .unwrap()
      .catch(handleApiError);

    if (!serviceOrder?.hasItems) {
      await submitDiscountDirectly(data);
      return;
    }

    const serviceOrderDiscount = buildServiceOrderDiscount(data);
    const originalDiscount = buildServiceOrderDiscount(defaultValues);

    if (JSON.stringify(serviceOrderDiscount) === JSON.stringify(originalDiscount)) {
      await submitDiscountDirectly(data);
      return;
    }

    await openDialog(
      <DiscountConfirmationDialogContent
        serviceCaseId={props.serviceCaseId}
        serviceOrderId={props.serviceOrderId}
        serviceOrderDiscount={serviceOrderDiscount}
        data-testid={suffixTestId('confirmationDialog', props)}
      />,
      {
        title: i18n.t('entity.order.discountConfirmation.title'),
        size: 'small',
      }
    );
  };

  const defaultValues: DiscountSectionFormType = {
    isWorkExpanded: isNotNil(props.data?.workDiscount),
    selectedWorkOption: isNil(props.data?.workDiscount?.discountedWorkRate)
      ? WorkDiscountOption.PercentageDiscount
      : WorkDiscountOption.DiscountedWorkRate,
    isMaterialExpanded: isNotNil(props.data?.materialDiscount),
    selectedMaterialOption: isNil(props.data?.materialDiscount?.markupToPurchasePrice)
      ? MaterialDiscountOption.DiscountOnSalePrice
      : MaterialDiscountOption.MarginToPurchasePrice,
    isSmallUnitMaterialExpanded: props.smallUnitMaterialData?.isFixedValueEnabled ?? false,
    isFixedValueWithVat: props.smallUnitMaterialData?.isFixedValueWithVat ?? false,
    work: {
      percentDiscount: {
        discountRate:
          getPercentageFromDecimal(props.data?.workDiscount?.percentageDiscount) ?? undefined,
      },
      discountedWorkRates: {
        discountedWorkRate: props.data?.workDiscount?.discountedWorkRate?.map((workRate) => ({
          workRateType: workRate?.workType ?? undefined,
          workRatePriceWithoutVat: workRate?.workRateWithoutVat?.amount ?? undefined,
        })) ?? [{workRateType: undefined, workRatePriceWithoutVat: undefined}],
      },
    },
    material: {
      percentDiscount: {
        discountOnSalePriceRate:
          getPercentageFromDecimal(props.data?.materialDiscount?.discountToSalePrice) ?? undefined,
        discountMarginToPurchasePriceRate:
          getPercentageFromDecimal(props.data?.materialDiscount?.markupToPurchasePrice) ??
          undefined,
      },
    },
    smallUnitMaterial: {
      fixedValueAmountWithoutVat:
        isNotNil(props.smallUnitMaterialData?.isFixedValueWithVat) &&
        isNotNil(props.smallUnitMaterialData?.fixedValueAmount) &&
        !props.smallUnitMaterialData?.isFixedValueWithVat
          ? props.smallUnitMaterialData?.fixedValueAmount
          : undefined,
      fixedValueAmountWithVat:
        isNotNil(props.smallUnitMaterialData?.isFixedValueWithVat) &&
        isNotNil(props.smallUnitMaterialData?.fixedValueAmount) &&
        props.smallUnitMaterialData?.isFixedValueWithVat
          ? props.smallUnitMaterialData?.fixedValueAmount
          : undefined,
    },
  };

  return (
    <Form<DiscountSectionFormType>
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      experimentalZodSchema={discountSectionSchema}
    >
      {(control, formApi) => (
        <VStack spacing={4}>
          <DiscountOnWorkCard
            serviceCaseId={props.serviceCaseId}
            control={control}
            formApi={formApi}
            currency={currency}
            isReadOnly={props.isReadOnly || props.isDisabled || !props.canEditDiscount}
            data-testid={suffixTestId('work', props)}
          />
          <DiscountOnMaterialCard
            control={control}
            formApi={formApi}
            isReadOnly={props.isReadOnly || props.isDisabled || !props.canEditDiscount}
            data-testid={suffixTestId('material', props)}
          />
          <SmallUnitMaterialCard
            control={control}
            formApi={formApi}
            currency={currency}
            whatIfAmountWithoutVat={props.smallUnitMaterialData?.whatIfAmountWithoutVat}
            whatIfAmountWithVat={props.smallUnitMaterialData?.whatIfAmountWithVat}
            isReadOnly={props.isReadOnly || props.isDisabled || !props.canEditDiscount}
            data-testid={suffixTestId('smallUnitMaterial', props)}
          />
          <Hide when={props.isReadOnly}>
            <ButtonGroup align="right">
              <Tooltip
                isDisabled={props.canEditDiscount}
                label={i18n.t('general.labels.noPermission')}
              >
                <FormButton
                  control={control}
                  type="submit"
                  isDisabled={!props.canEditDiscount}
                  title={i18n.t('general.actions.saveChanges')}
                  data-testid={suffixTestId('save', props)}
                />
              </Tooltip>
            </ButtonGroup>
          </Hide>
        </VStack>
      )}
    </Form>
  );
}
